import { Col, Drawer, Row } from 'antd';
import React from 'react';
import style from './style.module.scss';
import { Form, Formik } from 'formik';
import Select from './Select';
import {
  ApplicableForOptions,
  categoryOptions,
  deliveryTypeOptions,
  requirementTypeOptions,
  offer_type_options,
  offerTypeReverse,
} from './base';
import FieldInput from './FieldInput';
import DateFieldInput from './DateFieldInput';
import moment from 'moment';
import Stepper from './Stepper';
import * as Yup from 'yup';
import BottomChevron from 'assets/images/Settings/Icon/chevron-bottom.svg';
import StatusToggler from 'components/UI/Toggler/StatusToggler';
import UploadMediaWithS3 from './UploadMediaWithS3';

export const validationSchema = Yup.object().shape({
  category: Yup.array().min(1, 'Select at least one category').required('Category is required'),

  requirementType: Yup.array()
    .min(1, 'Select at least one fabric type')
    .required('Fabric type is required'),

  applicableFor: Yup.array()
    .min(1, 'Select at least one option')
    .required('Applicable for is required'),

  deliveryType: Yup.array()
    .min(1, 'Select at least one delivery type')
    .required('Delivery type is required'),

  minQuantity: Yup.number()
    .typeError('Minimum quantity must be a number')
    .min(500, 'Quantity cannot be less than 500')
    .required('Quantity is required'),

  minOrderValue: Yup.number()
    .typeError('Minimum order value must be a number')
    .min(10000, 'Order value cannot be less than 10000')
    .required('Min order value is required'),

  minMargin: Yup.number()
    .typeError('Minimum margin must be a number')
    .min(0.5, 'Margin cannot be less than 0.5%')
    .max(10, 'Margin cannot exceed 10%')
    .required('Min margin is required'),

  couponName: Yup.string().trim().required('Coupon name is required'),

  couponCode: Yup.string()
    .trim()
    .matches(/^[A-Z0-9_-]+$/, 'Should be uppercase and alphanumeric')
    .min(5, 'Should be more than 5 characters')
    .required('Coupon code is required'),

  offerType: Yup.number().typeError('Offer type is required').required('Offer type is required'),

  startDate: Yup.date().typeError('Start date is required').required('Start date is required'),

  endDate: Yup.date()
    .typeError('End date is required')
    .min(Yup.ref('startDate'), 'End date must be after start date')
    .required('End date is required'),

  discountAmount: Yup.number()
    .typeError('Discount value must be a number')
    .min(0, 'Discount must be greater than 0')
    .when('offerType', {
      is: offerTypeReverse.ABSOLUTE,
      then: (schema) =>
        schema
          .integer('Decimal values are not allowed for absolute discount')
          .required('Discount amount is required'),
      otherwise: (schema) => schema.required('Discount amount is required'),
    }),

  discountPercentage: Yup.number()
    .typeError('Discount value must be a number')
    .positive('Discount must be greater than 0')
    .when('offerType', {
      is: offerTypeReverse.PERCENTAGE,
      then: (schema) =>
        schema.max(100, 'Percentage cannot exceed 100').required('Discount percentage is required'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  maxUsagePerUser: Yup.number()
    .typeError('Usage must be a number')
    .integer('Usage must be an integer')
    .positive('Usage must be greater than 0')
    .required('Usage is required'),

  customerSheet: Yup.mixed()
    .required('Customer sheet is required')
    .test('fileType', 'Only CSV files are allowed', (value) => {
      if (!value) return false;

      if (typeof value === 'string') {
        return value.toLowerCase().endsWith('.csv');
      }

      return value.type === 'text/csv' || value.name?.toLowerCase().endsWith('.csv');
    }),
});

const DiscountCouponsForm = ({
  initialFormData,
  visible = false,
  onClose = () => {},
  createNewDiscountCoupon,
  reload,
}) => {
  const disabledDate = (current) => {
    return current && current < moment().startOf('day');
  };
  const disabledDateTime = () => {
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };

  const onSubmitForm = (values) => {
    const form = {
      couponName: values.couponName,
      couponCode: values.couponCode,
      offerType: Number(values.offerType),
      discountAmount: Number(values.discountAmount),
      discountPercentage: Number(values.discountPercentage),
      maxUsagePerUser: Number(values.maxUsagePerUser),
      startDate: values.startDate ? moment(values.startDate).utc().toISOString() : null,
      endDate: values.endDate ? moment(values.endDate).utc().toISOString() : null,
      minQuantity: Number(values.minQuantity),
      minMargin: Number(values.minMargin),
      minOrderValue: Number(values.minOrderValue),
      deliveryType: values.deliveryType,
      requirementType: values.requirementType,
      applicableFor: values.applicableFor,
      category: values.category,
      isForNewCustomers: values.isForNewCustomers,
      isVisible: values.isVisible,
      customerSheet: values.customerSheet,
    };

    createNewDiscountCoupon(form).then((res) => {
      if (res) {
        reload();
        onClose();
      }
    });
  };

  return (
    <Drawer
      className={style['coupon-drawer']}
      destroyOnClose
      width={550}
      title={'Add New Coupon'}
      open={visible}
      onClose={() => onClose()}
      bodyStyle={{ paddingBottom: 80 }}>
      <Formik
        enableReinitialize
        initialValues={{ ...initialFormData }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSubmitForm(values);
        }}>
        {({ values, errors, dirty, setFieldValue }) => {
          const disableCondition = !dirty || (dirty && Object.keys(errors).length > 0);

          return (
            <Form className={style['form']}>
              <Col className={style.section} span={24}>
                <p className={style['form-heading']}>Order specifications</p>

                <Row className={style['form-row']}>
                  <Col span={24} className={style['field-column']}>
                    <Select
                      isRequired
                      placeholder="Select here"
                      options={categoryOptions}
                      name="category"
                      isMultiSelect
                      label="Category"
                      suffixIcon={<img src={BottomChevron} alt="chevron icon" />}
                    />
                  </Col>

                  <Col span={24} className={style['field-column']}>
                    <Select
                      isRequired
                      placeholder="Select here"
                      options={requirementTypeOptions}
                      name="requirementType"
                      isMultiSelect
                      label="Requirement type"
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <Select
                      isRequired
                      placeholder="Select here"
                      options={ApplicableForOptions}
                      name="applicableFor"
                      isMultiSelect
                      label="Applicable for"
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <Select
                      isRequired
                      placeholder="Select here"
                      options={deliveryTypeOptions}
                      name="deliveryType"
                      isMultiSelect
                      label="Delivery type"
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <FieldInput
                      isRequired
                      name="minQuantity"
                      placeholder="Enter minimum quantity"
                      label="Minimum quantity"
                      subText="Quantity should be more than 500 meters"
                      type="number"
                      isUnit="meter"
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <FieldInput
                      isRequired
                      name="minOrderValue"
                      placeholder="Enter minimum order value here"
                      label="Minimum order value"
                      subLabel="(inclusive of taxes)"
                      subText="Min order value should be more than ₹10,000"
                      type="number"
                      isPrice
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <FieldInput
                      isRequired
                      name="minMargin"
                      placeholder="Enter minimum margin"
                      label="Minimum order margin"
                      subText="Min order margin should be between 0.5% and 10%"
                      type="number"
                      isUnit="%"
                    />
                  </Col>
                </Row>
              </Col>

              <Col className={style.section} span={24}>
                <p className={style['form-heading']}>Coupon specifications</p>

                <Row className={style['form-row']}>
                  <Col span={24} className={style['field-column']}>
                    <FieldInput
                      isRequired
                      name="couponName"
                      placeholder="e.g. Diwali offer"
                      label="Coupon name"
                      type="text"
                    />
                  </Col>

                  <Col span={24} className={style['field-column']}>
                    <FieldInput
                      isRequired
                      name="couponCode"
                      placeholder="e.g. DIWALI500OFF"
                      label="Coupon code"
                      subText="Should be alphanumeric and more than 5 characters"
                      type="text"
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <Select
                      isRequired
                      placeholder="Select here"
                      options={offer_type_options}
                      name="offerType"
                      label="Offer type"
                      // onChangeExtended={(val) => {
                      //   const value = Number(val);

                      //   if (value === offerTypeReverse.PERCENTAGE) {
                      //     setFieldValue('discountAmount', null);
                      //   } else {
                      //     setFieldValue('discountPercentage', null);
                      //   }
                      // }}
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <DateFieldInput
                      isRequired
                      placeholder="Select here"
                      options={deliveryTypeOptions}
                      name="startDate"
                      label="Start Date and Time"
                      disabledDate={disabledDate}
                      disabledTime={disabledDateTime}
                      showTime={{
                        defaultValue: moment('00:00:00', 'HH:mm:ss'),
                      }}
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <DateFieldInput
                      isRequired
                      placeholder="Select here"
                      options={deliveryTypeOptions}
                      name="endDate"
                      label="End Date and Time"
                      disabledDate={disabledDate}
                      showTime={{
                        defaultValue: moment('00:00:00', 'HH:mm:ss'),
                      }}
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <FieldInput
                      isRequired
                      name="discountAmount"
                      placeholder="Enter discount amount"
                      label="Discount amount"
                      isUnit={
                        values.offerType === offerTypeReverse.PERMETER ? '/ meter' : undefined
                      }
                      isPrice
                      // name={`${
                      //   values.offerType === offerTypeReverse.PERCENTAGE
                      //     ? 'discountPercentage'
                      //     : 'discountAmount'
                      // }`}
                      // placeholder={`Enter discount ${
                      //   values.offerType === offerTypeReverse.PERCENTAGE ? 'percentage' : 'amount'
                      // }`}
                      // label={`Discount ${
                      //   values.offerType === offerTypeReverse.PERCENTAGE ? 'percentage' : 'amount'
                      // }`}
                      // type="number"
                      // {...(values.offerType === offerTypeReverse.PERCENTAGE
                      //   ? { isUnit: '%' }
                      //   : { isPrice: true })}
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <Stepper
                      isRequired
                      name="maxUsagePerUser"
                      placeholder="Enter usage"
                      label="Usage"
                      type="number"
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <StatusToggler
                      label={'Is this coupon for new customers?'}
                      style={style}
                      onFalseLabel={values.isForNewCustomers ? 'Yes' : 'No'}
                      condition={values.isForNewCustomers}
                      onChange={() => {
                        setFieldValue('isForNewCustomers', !values.isForNewCustomers);
                      }}
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <StatusToggler
                      label={'Display this coupon to the customer?'}
                      style={style}
                      onFalseLabel={values.isVisible ? 'Yes' : 'No'}
                      condition={values.isVisible}
                      onChange={() => {
                        setFieldValue('isVisible', !values.isVisible);
                      }}
                    />
                  </Col>
                  <Col span={24} className={style['field-column']}>
                    <UploadMediaWithS3
                      isRequired
                      uploadButtonName="Upload customer sheet"
                      name="customerSheet"
                      label="Upload customer sheet"
                      acceptFileType=".csv"
                    />
                  </Col>
                </Row>
              </Col>
              <Row className={`${style['form-row']} ${style['footer']}`}>
                <Col span={12}></Col>

                <Col span={12}>
                  <div className={style['form-button']}>
                    <button type="button" className={style.cancel} onClick={onClose}>
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={disableCondition}
                      className={disableCondition ? style.saveDisabled : style.save}>
                      Create Coupon
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

export default DiscountCouponsForm;
